import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Inbox from '@mui/icons-material/Inbox'
import Percent from '@mui/icons-material/Percent'
import {
  Box,
  Divider,
  darken,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import { ThemePreview } from '@workwave-tidal/core/devtools'
import { ColorLookup } from './components/ColorLookup/ColorLookup.js'
import { DarkModeToggler } from './components/DarkModeToggler.js'
import { Drawer } from './components/Drawer.js'
import { WarnTooltip } from './components/WarnTooltip.js'
import { useThemeSidebar } from './hooks/useThemeSidebar.js'

const EmptyBox = () => <Box sx={{ width: 0 }} />

export function ThemeSidebar() {
  const {
    themeModified,
    onResetTheme,
    //
    onToggleSidebar,
    sidebarOpen,
    //
    onClickCoverage,
  } = useThemeSidebar()

  return (
    <Drawer data-id="theme-sidebar-drawer" open={sidebarOpen} variant="permanent">
      <Box data-testid="vertical-spacer" sx={{ flex: '1 1 auto' }} />

      {/*
        ------------------------------------------------------
        THEME EDITOR
        ------------------------------------------------------
      */}
      {sidebarOpen ? <ThemePreview onClose={onToggleSidebar} /> : <EmptyBox />}

      <Divider data-testid="divider" />

      <List data-testid="actions" sx={{ padding: 0 }}>
        {/*
          ------------------------------------------------------
          RESET MODIFIED THEME BUTTON
          ------------------------------------------------------
        */}

        <DarkModeToggler sidebarOpen={sidebarOpen} />
        <ColorLookup sidebarOpen={sidebarOpen} />
        {themeModified && (
          <>
            <WarnTooltip arrow placement="right" title={sidebarOpen ? '' : 'Reset Active Theme'}>
              <ListItem
                data-testid="reset-theme"
                disablePadding
                onClick={onResetTheme}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: 0,
                }}
              >
                <ListItemButton
                  sx={{
                    '&:hover': th => ({
                      backgroundColor: darken(th.palette.error.dark, 0.3),
                    }),
                    backgroundColor: th => th.vars.palette.error.dark,
                    justifyContent: sidebarOpen ? 'initial' : 'center',
                    minHeight: 48,
                    padding: 0,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: th => th.vars.palette.tidalColors.cloudGray[50],
                      justifyContent: 'center',
                      minWidth: 0,
                      px: 1,
                    }}
                  >
                    <Inbox />
                  </ListItemIcon>
                  <ListItemText
                    primary="Reset Active Theme"
                    primaryTypographyProps={{
                      sx: {
                        color: th => th.vars.palette.tidalColors.cloudGray[50],
                      },
                    }}
                    sx={{ opacity: sidebarOpen ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </WarnTooltip>

            <Divider data-testid="divider" />
          </>
        )}

        {/*
          ------------------------------------------------------
          Tidal Coverage
          ------------------------------------------------------
        */}

        <Tooltip arrow placement="right" title={sidebarOpen ? '' : 'Coverage'}>
          <ListItem
            data-testid="toggle-coverage"
            disablePadding
            onClick={onClickCoverage}
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-end',
              padding: 0,
            }}
          >
            <ListItemButton
              sx={{
                justifyContent: sidebarOpen ? 'initial' : 'center',
                minHeight: 48,
                px: 0,
              }}
            >
              <ListItemIcon
                sx={{
                  justifyContent: sidebarOpen ? 'flex-end' : 'center',
                  minWidth: 0,
                  px: 1,
                  width: '100%',
                }}
              >
                <Percent />
              </ListItemIcon>
              <ListItemText sx={{ opacity: sidebarOpen ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        </Tooltip>

        {/*
          ------------------------------------------------------
          TOGGLE SIDEBAR BUTTON
          ------------------------------------------------------
        */}

        <Tooltip arrow placement="right" title={sidebarOpen ? '' : 'Open Editor'}>
          <ListItem
            data-testid="toggle-sidebar"
            disablePadding
            onClick={onToggleSidebar}
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-end',
              padding: 0,
            }}
          >
            <ListItemButton
              sx={{
                justifyContent: sidebarOpen ? 'initial' : 'center',
                minHeight: 48,
                px: 0,
              }}
            >
              <ListItemIcon
                sx={{
                  justifyContent: sidebarOpen ? 'flex-end' : 'center',
                  minWidth: 0,
                  px: 1,
                  width: '100%',
                }}
              >
                {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
              </ListItemIcon>
              <ListItemText sx={{ opacity: sidebarOpen ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        </Tooltip>
      </List>
    </Drawer>
  )
}
